import { createProductLogger } from '@myua/product-logger';
import { createSelector } from '@reduxjs/toolkit';
import { getUser } from './selectors';

let logger: ReturnType<typeof createProductLogger>;

export const getAssetLabLogger = createSelector(getUser, (user) => {
  return (record: ProductLog) => {
    if (!user) {
      return;
    }
    if (!logger) {
      logger = createProductLogger({ service: 'assetlab' });
    }
    logger.log(record);
  };
});

export type AssetLabLogger = (record: ProductLog) => void;

export type ProductLog =
  | { action: 'Service open' }
  | {
      action: 'Upload';
      sub_action: 'File';
    }
  | {
      action: 'Upload';
      sub_action: 'Complete';
      extra: {
        int_1: number; // node_id after upload
        text_1: string; // name of the file
        int_2: number | null; // Time in milliseconds
        int_3: number; // Size in bytes
      };
    }
  | {
      action: 'File';
      sub_action: 'Delete';
      extra: {
        int_1: number; // node_id
      };
    }
  | {
      action: 'Left sidebar';
      sub_action: 'Click button';
      extra: {
        text_1: string; // button
      };
    }
  | {
      action: 'Favorite';
      sub_action: 'Toggle';
      extra: {
        // asset_ids: number[];
        bool_1: boolean; // checked
        text_1: 'single' | 'multiple' | 'all'; // mode
      };
    }
  | { action: 'Favorite'; sub_action: 'Clear' }
  | {
      action: 'Pack';
      sub_action: 'Toggle';
      extra: {
        // asset_ids: number[]; // asset_ids
        bool_1: boolean; // checked
        text_1: 'single' | 'multiple' | 'all'; // mode
      };
    }
  | { action: 'Pack'; sub_action: 'Clear' }
  | { action: 'Pack'; sub_action: 'Download' }
  | { action: 'Pack'; sub_action: 'Share' }
  | {
      action: 'Assets';
      sub_action: 'Download';
      extra: {
        // asset_ids: number[];
        text_1: 'button' | 'fullscreen'; // location
      };
    }
  | {
      action: 'Assets';
      sub_action: 'Share';
      extra: {
        // asset_ids: number[];
        text_1: 'single' | 'multiple' | 'all'; // mode
      };
    }
  | {
      action: 'Sharing';
      sub_action: 'Download';
      extra: {
        text_1: string; // sharing_id
      };
    }
  | {
      action: 'Sharing';
      sub_action: 'Share';
      extra: {
        text_1: string; // sharing_id
      };
    }
  | {
      action: 'Sharing';
      sub_action: 'Delete links';
    }
  | {
      action: 'Sharing';
      sub_action: 'Restore links';
    }
  | {
      action: 'Sharing';
      sub_action: 'Edit link';
      extra: {
        text_1: string; // sharing_id
        text_2: string; // name
      };
    }
  | {
      action: 'Filter';
      sub_action: 'Change project';
      // extra: {
      //   text_1: string; // project_id
      // };
    }
  | {
      action: 'Filter';
      sub_action: 'Search by text';
      extra: {
        text_1: string; // text
        bool_1: boolean; // search_in_layers
        bool_2: boolean; // search_in_archives
        bool_3: boolean; // search_with_ai
        int_1: number | null; // search_upload_id
        int_2: number | null; // similar asset id
        text_2: string | null; // file name or similar asset name
      };
    }
  | {
      action: 'Filter';
      sub_action: 'Change AI search mode';
      extra: {
        bool_1: boolean; // is active
        context: 'icon' | 'options';
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Add';
      extra: {
        int_1: number | null; // asset id (null for a captured frame)
        text_1: 'sidebar' | 'fullscreen' | 'capture'; // where the click came from
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Open';
      extra: {
        int_1: number; // number of references staged in the basket
        text_1: 'sidebar' | 'fullscreen' | 'tray'; // where the click came from
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Connect API Key';
      extra: {
        bool_1: boolean; // true = updated an existing key, false = first connect
        // Which provider the key is for ('gemini' | 'openai'). Only sent on
        // the backend path (MYUA-16447); the legacy direct path is Gemini-only
        // and omits it.
        text_1?: string;
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Generate';
      extra: {
        int_1: number; // number of references in the session
        text_1: string; // model id
        text_2: 'key' | 'backend' | 'demo'; // generation path
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Generate Result';
      extra: {
        text_1: 'Success' | 'Fail'; // result status
        text_2: 'key' | 'backend' | 'demo'; // generation path
        text_3: string; // model id
        int_1: number | null; // total tokens used (if reported)
        int_2: number | null; // prompt tokens (if reported)
        int_3: number | null; // output tokens (if reported)
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Upscale';
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Remove Background';
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Save Generation';
      extra: {
        text_1: string; // model id
        text_2: 'generation' | 'upscale' | 'removeBg'; // what produced the saved image
        int_1: number; // number of references in the session
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Save to Assets';
      extra: {
        int_1: number; // created asset id
        text_1: string; // model id
        text_2: string; // destination category id
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Remix';
      extra: {
        text_1: 'new_from_result' | 'reuse_setup'; // which remix action
      };
    }
  | {
      action: 'Magic Studio';
      sub_action: 'Delete Generation';
    }
  | {
      action: 'Magic Studio';
      sub_action: 'View History';
    }
  // Explicitly discarding a parked session ("New session") — closing the studio
  // keeps it, so this is the only place work is thrown away on purpose.
  | {
      action: 'Magic Studio';
      sub_action: 'New Session';
    };
